/**
 * No Mixpanel event may carry a secret or a person's data. The dashboard's own
 * properties are ids and enums; what can carry either is a URL the SDK attaches to
 * every event by itself ($current_url, the referrers), and the dashboard's URLs do:
 *   - `?auth_token=…`, a magic-link login token (Router);
 *   - `?token=…`, a password-reset token (ResetPassword);
 *   - Stripe's `setup_intent_client_secret` on the card-setup return (PaymentMethodForm),
 *     and its Checkout `checkout_session_id` (partner billing);
 *   - `?search=…` on the reservations list, which is a guest's name as often as not.
 * A referrer can carry whatever another site puts in its URLs. A denylist cannot keep
 * up with that, nor with the next screen that reads a parameter, so the query is
 * ALLOWLISTED: every parameter is dropped except the keys below. The fragment is
 * dropped too: the dashboard never reads it (BrowserRouter), and it is where
 * implicit-grant tokens travel.
 *
 * Mirrors shop-web's scrubPersonalData.ts; only the allowlist differs.
 */

/**
 * The only query parameters a URL keeps: campaign / click ids, and the dashboard's
 * own enums and flags that say where on a screen the event happened. Never a
 * person's data or a credential. Matched exactly (after decoding the key).
 */
const SAFE_QUERY_KEYS = new Set([
  // Campaign attribution. Mixpanel reads these into its own utm_* properties from
  // the page address itself, so they are attributed either way.
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // Ad click ids (Google, Meta).
  'gclid',
  'fbclid',
  // The tab a screen opens on (takeout settings, promo codes).
  'tab',
  // The wizard phase an Online payment return lands on, and the return marker.
  'phase',
  'payment_return',
  // Back from Stripe Checkout at the paywall: the product key, and the cancel marker.
  'paywall_card',
  'paywall_card_cancelled',
  // Stripe's outcome on a return URL (succeeded / failed …).
  'redirect_status',
]);

/** The URL-valued properties the Mixpanel SDK adds to events by itself. */
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_referrer', 'current_url_search'];

/**
 * Properties the SDK derives from a URL's query that are not URLs themselves, removed
 * with the SDK's `property_blacklist`: mp_keyword is the search query of a
 * search-engine referrer.
 */
export const URL_DERIVED_PROPERTY_BLACKLIST = ['mp_keyword'];

function queryKey(pair) {
  try {
    return decodeURIComponent(pair.split('=')[0].replace(/\+/g, ' '));
  } catch {
    // A malformed escape is not an allowlisted key.
    return null;
  }
}

/**
 * `url` without its fragment and without every query parameter that is not
 * allowlisted. Returned unchanged (byte for byte) when there is nothing to remove, so
 * ordinary URLs keep their exact encoding. Also takes a bare query (`?a=b`, the SDK's
 * current_url_search).
 */
export function scrubUrl(url) {
  const hashStart = url.indexOf('#');
  const withoutHash = hashStart === -1 ? url : url.slice(0, hashStart);
  const queryStart = withoutHash.indexOf('?');

  if (queryStart === -1) {
    return withoutHash;
  }

  const pairs = withoutHash.slice(queryStart + 1).split('&');
  const kept = pairs.filter((pair) => SAFE_QUERY_KEYS.has(queryKey(pair)));

  if (kept.length === pairs.length) {
    return withoutHash;
  }

  const base = withoutHash.slice(0, queryStart);

  return kept.length ? `${base}?${kept.join('&')}` : base;
}

/** Scrubs the URL properties of an event's property bag in place, and returns it. */
export function scrubUrlProperties(properties) {
  URL_PROPERTIES.forEach((key) => {
    if (typeof properties[key] === 'string') {
      properties[key] = scrubUrl(properties[key]);
    }
  });

  return properties;
}

/**
 * The SDK keeps the first-touch referrer (`$initial_referrer`) raw in its persistence,
 * sends it on every event as a super property and once to the people profile (on
 * load). This rewrites it scrubbed before the SDK reads it. When it is not recorded
 * yet, the SDK is about to record `document.referrer` (or `$direct`) with a
 * register_once, so the scrubbed version of that is registered first and the SDK's
 * register_once leaves it alone.
 */
export function sanitizeInitialReferrer(mp) {
  const current = mp.get_property('$initial_referrer');
  const source = typeof current === 'string' && current !== '' ? current : document.referrer || '$direct';
  const scrubbed = scrubUrl(source);

  if (scrubbed !== current) {
    mp.register({ $initial_referrer: scrubbed });
  }
}

/**
 * Mixpanel `before_track` hook. It runs inside track() before the SDK merges in its
 * own properties and QUEUES the event, which is why the scrubbing has to happen here
 * and not only in before_send_events: the SDK keeps queued events in localStorage and,
 * when a tab closed before they were confirmed sent, re-sends them from a later page
 * load with the before_send hooks skipped ("orphaned" items). An event property
 * overrides the SDK's own of the same name, so the scrubbed URLs are what gets queued.
 */
export function scrubUrlsBeforeTrack(mp) {
  return (eventName, properties) => {
    sanitizeInitialReferrer(mp);

    const scrubbed = scrubUrlProperties({ ...properties });

    scrubbed.$current_url = scrubUrl(scrubbed.$current_url ?? window.location.href);

    if (document.referrer) {
      scrubbed.$referrer = scrubUrl(scrubbed.$referrer ?? document.referrer);
    }

    return [eventName, scrubbed];
  };
}

/**
 * Mixpanel `before_send_events` hook: the last pass over an event's URL properties
 * just before it is sent. Always returns the payload — returning nothing would make
 * the SDK drop the event.
 */
export function scrubUrlsBeforeSend(payload) {
  if (payload?.properties) {
    scrubUrlProperties(payload.properties);
  }

  return payload;
}
